'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import Container from '@/components/ui/Container/Container';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import CmsImage from '@/components/ui/CmsImage';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { mapCmsBlogToUi } from '@/lib/blog-mappers';
import { stripHtml } from '@/lib/homepage-mappers';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import type { BlogPost as CmsBlogPost } from '@/types/blog';

const FALLBACK_LEFT_IMAGE = '/images/hm_blog1.jpg';
const FALLBACK_RIGHT_IMAGE = '/images/monsson1.png';

interface HomeMediaCoverageProps {
  /**
   * CMS blogs to display in the carousel. Sourced from `getBlogLanding`
   * (or `getAllBlogs`) at the page level and drilled down through
   * `HomeClient` → `Home` → here. When empty or omitted (network error,
   * empty CMS), the section quietly renders nothing rather than a stale
   * hardcoded list — keeps the homepage from ever shipping fake content.
   */
  blogs?: CmsBlogPost[];
  /**
   * Section sub-heading. Comes from the CMS `blog-all.description` HTML
   * field (stripped to plain text). Falls back to the i18n translation
   * when the CMS doesn't return a description.
   */
  description?: string | null;
  locale?: string;
}

export default function HomeMediaCoverage({
  blogs = [],
  description,
  locale = 'en',
}: HomeMediaCoverageProps) {
  const t = useTranslations('home.blogs');
  // Prefer CMS-supplied description (HTML — strip tags) over the local
  // translation. Trim because Strapi wraps single-line copy in <p>…</p>
  // which leaves no extra whitespace, but a blank string slips through if
  // the editor accidentally publishes "<p></p>".
  const cmsDescription = stripHtml(description ?? '').trim();
  const resolvedDescription = cmsDescription || t('description');

  // Map CMS payload → UI shape (title, excerpt, slug, image, etc.). If
  // there are no blogs, drop the carousel entirely so the section just
  // shows the heading and "Explore More Blogs" CTA.
  const items = blogs.map(mapCmsBlogToUi);
  const hasItems = items.length > 0;

  return (
    <section className="bg-[#f8f7f4] py-12 md:py-16 lg:py-20">
      <Container size="full" className="!px-0 max-sm:!px-4">
        <div className="container mx-auto mb-8 flex flex-col gap-6 px-4 sm:px-6 md:mb-14 md:flex-row md:items-end md:justify-between lg:px-0">
          <div className="flex max-w-3xl flex-col">
            <h2 className="text-3xl font-semibold leading-tight text-[#0A0A0A] sm:text-4xl md:text-5xl lg:text-6xl">
              {t('heading')}
            </h2>
            <p className="mt-4 max-w-2xl text-xs leading-relaxed text-[#0A0A0A]/85 sm:text-sm">
              {resolvedDescription}
            </p>
          </div>
          <div className="flex justify-start md:justify-center">
            <SwapTextButton
              variant="outline"
              text={t('exploreCta')}
              href={`/${locale}/blog`}
              showArrow
            />
          </div>
        </div>

        {hasItems && (
          <div className="mx-auto w-full">
            <Carousel opts={{ align: 'start', loop: true }} className="w-full">
              <CarouselContent>
                {items.map((blog, idx) => {
                  // Pair each blog's featured image with the NEXT blog's image
                  // on the right column so the layout always feels fresh —
                  // falls back to a static decorative image if there's only
                  // one CMS entry.
                  const nextBlog = items[(idx + 1) % items.length];
                  const rightImage = items.length > 1 ? nextBlog.image : FALLBACK_RIGHT_IMAGE;
                  const rightImageAlt = items.length > 1 ? nextBlog.imageAlt : '';
                  const detailHref = `/${locale}/blog/${blog.slug}`;

                  return (
                    <CarouselItem key={blog.id} className="basis-full">
                      <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-3">
                        <Link
                          href={detailHref}
                          className="group relative block min-h-[260px] overflow-hidden rounded-lg sm:min-h-[320px] lg:min-h-[420px]"
                          aria-label={blog.title}
                        >
                          <CmsImage
                            src={blog.image || FALLBACK_LEFT_IMAGE}
                            alt={blog.imageAlt || blog.title}
                            fill
                            sizes="(max-width: 1024px) 100vw, 33vw"
                            className="object-cover transition-transform duration-500 group-hover:scale-105"
                            fallbackSrc={MEDIA_PLACEHOLDER}
                          />
                        </Link>

                        <div className="flex min-h-[260px] flex-col justify-between rounded-lg bg-transparent px-1 py-2 sm:min-h-[320px] sm:px-0 md:p-6 md:py-0 lg:min-h-[420px]">
                          <div>
                            <Link href={detailHref} className="block group">
                              <h3 className="text-lg leading-tight text-[#0A0A0A] transition-colors group-hover:text-[#ed1c22] md:text-xl md:leading-[1.67]">
                                {blog.title}
                              </h3>
                            </Link>
                            {blog.excerpt && (
                              <p className="mt-3 text-xs leading-relaxed text-[#0A0A0A]/75 sm:text-sm">
                                {blog.excerpt}
                              </p>
                            )}
                          </div>
                          <div className="mt-6 flex items-center justify-between">
                            <SwapTextButton
                              variant="outline"
                              text={t('featured.ctaText')}
                              href={detailHref}
                              showArrow
                              className="border-0 bg-[#d9d9d9] px-4 py-2 text-xs !text-black hover:bg-[#cfcfcf] hover:!text-white"
                            />
                            <div className="hidden items-center gap-2 md:flex">
                              <CarouselPrevious className="static !h-9 !w-9 translate-y-0 !rounded-lg !border-0 !bg-white !p-0 text-black shadow-none hover:!bg-white/90 hover:text-black" />
                              <CarouselNext className="static !h-9 !w-9 translate-y-0 !rounded-lg !border-0 !bg-white !p-0 text-black shadow-none hover:!bg-white/90 hover:text-black" />
                            </div>
                          </div>
                        </div>

                        <Link
                          href={detailHref}
                          className="group relative block min-h-[260px] overflow-hidden rounded-lg sm:min-h-[320px] lg:min-h-[420px]"
                          aria-hidden
                          tabIndex={-1}
                        >
                          <CmsImage
                            src={rightImage}
                            alt={rightImageAlt}
                            fill
                            sizes="(max-width: 1024px) 100vw, 33vw"
                            className="object-cover transition-transform duration-500 group-hover:scale-105"
                            fallbackSrc={MEDIA_PLACEHOLDER}
                          />
                        </Link>
                      </div>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>

              <div className="mt-5 flex items-center justify-center gap-3 md:hidden">
                <CarouselPrevious className="static !h-9 !w-9 translate-y-0 !rounded-lg !border-0 !bg-white !p-0 text-black shadow-none hover:!bg-white/90 hover:text-black" />
                <CarouselNext className="static !h-9 !w-9 translate-y-0 !rounded-lg !border-0 !bg-white !p-0 text-black shadow-none hover:!bg-white/90 hover:text-black" />
              </div>
            </Carousel>
          </div>
        )}
      </Container>
    </section>
  );
}
